import { labsPalette } from "../labs/palette.js";
import React, { useLayoutEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import styled from "styled-components";
import LabsHeader from "../Components/LabsHeader.jsx";
import { LAB_SERVICES } from "../labs/services.js";
import { gradientBorder } from "../styles/mixin.js";
import {
  DEFAULT_LAB_VIEW, LAB_PAGE_SIZE, LAB_RECENT_KEY, LAB_SAVED_KEY, LAB_VIEW_KEY,
  labFreshness, publishedLabServices, readLabIds, readLabView, selectLabServices, writeLabStorage,
} from "../utils/labs.js";

function BookmarkIcon({ saved }) {
  return <svg width="20" height="20" viewBox="0 0 24 24" fill={saved ? "currentColor" : "none"} stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M6 4a1 1 0 0 1 1-1h10a1 1 0 0 1 1 1v17l-6-4-6 4Z" /></svg>;
}

export default function LabsPage({ services = LAB_SERVICES, catalogStatus, onRefresh }) {
  const [filters, setFilters] = useState(readLabView);
  const [savedIds, setSavedIds] = useState(() => readLabIds(LAB_SAVED_KEY));
  const [recentIds] = useState(() => readLabIds(LAB_RECENT_KEY));
  const [notice, setNotice] = useState("");
  const [filtersOpen, setFiltersOpen] = useState(false);
  const location = useLocation();
  const resultsHeading = useRef(null);
  const searchInput = useRef(null);
  const now = Date.now();
  const published = publishedLabServices(services, now);
  const canBrowse = published.length > 1;
  const catalogIds = published.map((service) => service.id);
  const catalogKey = catalogIds.join("|");
  const categories = [...new Set(published.map((service) => service.category))].sort((a, b) => a.localeCompare(b, "ko"));
  // A new publication must not be hidden by filters saved against the old catalogue.
  const resetView = !canBrowse || catalogIds.some((id) => !filters.catalogIds.includes(id));
  const activeFilters = { ...(resetView ? DEFAULT_LAB_VIEW : filters), catalogIds };
  if (!categories.includes(activeFilters.category)) activeFilters.category = "all";
  const matches = selectLabServices(published, activeFilters, savedIds, recentIds, now);
  const pageCount = Math.max(1, Math.ceil(matches.length / LAB_PAGE_SIZE));
  const page = Math.min(activeFilters.page, pageCount);
  const shown = matches.slice((page - 1) * LAB_PAGE_SIZE, page * LAB_PAGE_SIZE);
  const currentView = useRef(filters);
  currentView.current = { ...activeFilters, page };

  useLayoutEffect(() => {
    if (resetView) window.scrollTo(0, 0);
  }, [catalogKey, resetView]);

  function savePosition() {
    writeLabStorage(LAB_VIEW_KEY, { ...currentView.current, scrollY: window.scrollY }, true);
  }

  useLayoutEffect(() => {
    const frame = requestAnimationFrame(() => window.scrollTo(0, currentView.current.scrollY));
    return () => {
      cancelAnimationFrame(frame);
      writeLabStorage(LAB_VIEW_KEY, { ...currentView.current, scrollY: window.scrollY }, true);
    };
  }, []);

  function update(patch) {
    const next = { ...currentView.current, ...patch, catalogIds, page: patch.page || 1, scrollY: window.scrollY };
    setFilters(next);
    writeLabStorage(LAB_VIEW_KEY, next, true);
  }

  function toggleSaved(service) {
    const saved = savedIds.includes(service.id);
    const next = saved ? savedIds.filter((id) => id !== service.id) : [...savedIds, service.id];
    setSavedIds(next);
    writeLabStorage(LAB_SAVED_KEY, next);
    setNotice(saved ? `${service.name} 저장을 해제했어요.` : `${service.name}을 저장했어요.`);
  }

  function goToPage(next) {
    update({ page: next });
    resultsHeading.current?.focus({ preventScroll: true });
    resultsHeading.current?.scrollIntoView({ block: "start" });
  }

  const tabs = [
    { id: "all", label: "전체", count: published.length },
    { id: "new", label: "최근 추가", count: published.filter((service) => labFreshness(service, now) === "새 서비스").length },
    { id: "saved", label: "저장한", count: published.filter((service) => savedIds.includes(service.id)).length },
    { id: "recent", label: "최근 본", count: published.filter((service) => recentIds.includes(service.id)).length },
  ];
  const hasFilters = activeFilters.query.trim() || activeFilters.category !== "all";
  const hasOptions = activeFilters.category !== "all" || activeFilters.view !== "all" || activeFilters.sort !== "newest";
  const emptyTitle = hasFilters ? "딱 맞는 서비스를 찾지 못했어요." : activeFilters.view === "saved" ? "다시 쓸 서비스는 저장해 두세요." : activeFilters.view === "recent" ? "열어본 서비스를 여기서 다시 찾아요." : activeFilters.view === "new" ? "최근 7일에 추가된 서비스가 없어요." : "새 서비스를 준비하고 있어요.";

  return <Shell data-behavior="labs-catalog">
    <Helmet>
      <title>카톡 증거 정리·돈 받는 대화 연습 | 로우로우 랩스</title>
      <meta name="description" content="카톡은 시간순으로 정리하고, 못 받은 돈은 요구할 말부터 연습하세요. 가입 없이 미리 보고 내 상황에 맞춰 시작할 수 있습니다." />
      <link rel="canonical" href="https://www.lawlow.info/lab" />
    </Helmet>
    <LabsHeader />
    <Content>
      <IntroRow $canBrowse={canBrowse}>
      <Hero>
        <h1>증거부터, <Highlight>보낼 말까지.</Highlight></h1>
        <p>카톡은 시간순으로 정리하고, 못 받은 돈은 요구할 말부터 연습하세요.</p>
      </Hero>
      {canBrowse && <SearchForm role="search" onSubmit={(event) => { event.preventDefault(); searchInput.current?.blur(); resultsHeading.current?.focus(); }}>
        <label className="sr-only" htmlFor="labs-search">어떤 문제를 해결하고 싶나요?</label>
        <SearchBox>
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><circle cx="10.5" cy="10.5" r="6.5" /><path d="m16 16 5 5" /></svg>
          <input ref={searchInput} id="labs-search" type="search" enterKeyHint="search" autoComplete="off" maxLength={100} value={activeFilters.query} onChange={(event) => update({ query: event.target.value })} placeholder="보증금, 못 받은 돈, 답장 검색" />
          {activeFilters.query && <button type="button" aria-label="검색어 지우기" onClick={() => { update({ query: "" }); searchInput.current?.focus(); }}>×</button>}
        </SearchBox>
      </SearchForm>}
      </IntroRow>
      {onRefresh && <CatalogNotice>
        <span role="status">{catalogStatus === "loading" ? "서비스 목록을 확인하고 있어요…" : catalogStatus === "error" ? "목록을 새로 불러오지 못했어요. 잠시 후 다시 시도해 주세요." : ""}</span>
        <FilterButton type="button" onClick={onRefresh} disabled={catalogStatus === "loading"}>서비스 목록 새로고침</FilterButton>
      </CatalogNotice>}
      <Directory aria-label="서비스 둘러보기">
        <Toolbar>
          <h2 ref={resultsHeading} tabIndex={-1}>{canBrowse ? "서비스" : "바로 시작하기"}{canBrowse && <span>{matches.length}</span>}</h2>
          {canBrowse && <FilterButton type="button" aria-expanded={filtersOpen} aria-controls="labs-filters" onClick={() => setFiltersOpen((open) => !open)}>필터·정렬{hasOptions ? " · 적용 중" : ""} <span aria-hidden="true">{filtersOpen ? "−" : "+"}</span></FilterButton>}
        </Toolbar>
        {canBrowse && filtersOpen && <FilterPanel id="labs-filters">
          <Tabs role="group" aria-label="서비스 목록 종류">
            {tabs.map((tab) => <button key={tab.id} type="button" aria-pressed={activeFilters.view === tab.id} onClick={() => update({ view: tab.id })}>{tab.label}<span>{tab.count}</span></button>)}
          </Tabs>
          <FilterOptions>
          <Selectors>
            <label><span className="sr-only">서비스 분야</span><select aria-label="서비스 분야" value={activeFilters.category} onChange={(event) => update({ category: event.target.value })}><option value="all">모든 분야</option>{categories.map((category) => <option key={category} value={category}>{category}</option>)}</select></label>
            {activeFilters.view !== "recent" && <label><span className="sr-only">서비스 정렬</span><select aria-label="서비스 정렬" value={activeFilters.sort} onChange={(event) => update({ sort: event.target.value })}><option value="newest">최신순</option><option value="name">이름순</option></select></label>}
          </Selectors>
          {hasOptions && <FilterButton type="button" onClick={() => update({ category: "all", view: "all", sort: "newest" })}>초기화</FilterButton>}
          </FilterOptions>
          {activeFilters.view !== "all" && <ContextNote>{activeFilters.view === "new" ? "최근 7일에 새로 나온 서비스를 모았어요." : activeFilters.view === "saved" ? "저장 목록은 이 브라우저에 보관돼요." : "이 브라우저에서 최근 열어본 순서예요."}</ContextNote>}
        </FilterPanel>}
        <span className="sr-only" role="status">{matches.length}개 서비스{matches.length > 0 ? `, ${page} / ${pageCount} 페이지` : ""}</span>
        {shown.length ? <Grid $single={published.length === 1}>
          {shown.map((service) => {
            const saved = savedIds.includes(service.id);
            const freshness = labFreshness(service, now);
            return <Card key={service.id}>
              <CardMeta><div><span>{service.category}</span>{freshness && <Freshness>{freshness}</Freshness>}</div><SaveButton type="button" $saved={saved} aria-label={`${service.name} ${saved ? "저장 해제" : "저장"}`} aria-pressed={saved} onClick={() => toggleSaved(service)}><BookmarkIcon saved={saved} /></SaveButton></CardMeta>
              <CardLink to={{ pathname: service.path, search: location.search }} reloadDocument={service.reloadDocument} onClick={savePosition}>
                <h3>{service.name}</h3>
                <Problem>{service.problem}</Problem>
                <Description>{service.description}</Description>
                <Output>{service.output}</Output>
                <Cost><strong>{service.access}</strong><span>{service.price}</span></Cost>
                <CardAction>{service.action}<span aria-hidden="true">→</span></CardAction>
              </CardLink>
            </Card>;
          })}
        </Grid> : <EmptyState>
          <h3>{emptyTitle}</h3>
          <p>{hasFilters ? "검색어를 짧게 바꾸거나, 분야를 넓혀보세요." : activeFilters.view === "saved" ? "서비스 카드의 책갈피를 누르면 여기에 모여요." : activeFilters.view === "recent" ? "서비스를 둘러보고, 필요할 때 다시 돌아오세요." : "지금 이용할 수 있는 다른 서비스를 둘러보세요."}</p>
          {(hasFilters || activeFilters.view !== "all") && <button type="button" onClick={() => { update({ ...DEFAULT_LAB_VIEW }); resultsHeading.current?.focus(); }}>전체 서비스 보기</button>}
        </EmptyState>}
        {pageCount > 1 && <Pagination aria-label="서비스 목록 페이지">
          <button type="button" disabled={page === 1} onClick={() => goToPage(page - 1)}>← 이전</button>
          <label><select aria-label="서비스 페이지" value={page} onChange={(event) => goToPage(Number(event.target.value))}>{Array.from({ length: pageCount }, (_, index) => <option key={index + 1} value={index + 1}>{index + 1}</option>)}</select> / {pageCount}</label>
          <button type="button" disabled={page === pageCount} onClick={() => goToPage(page + 1)}>다음 →</button>
        </Pagination>}
        <span className="sr-only" role="status">{notice}</span>
      </Directory>
      <Footer><p>내 상황과 비슷한 판례도 궁금하다면</p><Link to="/main">로우로우에서 판례 찾기 <span aria-hidden="true">↗</span></Link></Footer>
    </Content>
  </Shell>;
}

const Shell = styled.div`
  min-height: 100vh; min-height: 100dvh; background: ${labsPalette.background}; color: ${labsPalette.text};
  * { box-sizing: border-box; }
  button, input, select { font: inherit; }
  button, a { -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
  button { cursor: pointer; }
  button:disabled { cursor: default; opacity: .4; }
  :focus-visible { outline: 2px solid #008a4c; outline-offset: 4px; }
  .sr-only { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }
`;
const Content = styled.main`max-width: 1160px; margin: 0 auto; padding: 0 24px max(32px, env(safe-area-inset-bottom)); @media (max-width: 599px) { padding-left: 20px; padding-right: 20px; }`;
const CatalogNotice = styled.div`display: flex; justify-content: space-between; align-items: center; gap: 12px; margin: 0 0 16px; color: #616963; font-size: 13px; line-height: 1.6; flex-wrap: wrap;`;
const IntroRow = styled.div`display: grid; grid-template-columns: ${({ $canBrowse }) => $canBrowse ? "1.15fr 1fr" : "1fr"}; align-items: center; gap: 44px; margin: 20px 0 24px; @media (max-width: 799px) { display: block; margin: 12px 0 20px; }`;
const Hero = styled.section`
  padding: 12px 0;
  h1 { margin: 0 0 10px; font-size: clamp(24px, 2.5vw, 30px); font-weight: 750; line-height: 1.4; letter-spacing: -1px; word-break: keep-all; }
  p { color: #616963; font-size: 14px; line-height: 1.6; margin: 0; word-break: keep-all; }
  @media (max-width: 599px) { padding: 8px 0 16px; p { font-size: 13px; } }
`;
const Highlight = styled.span`background: linear-gradient(100deg, #daffee, #e0eaff) bottom / 100% 45% no-repeat;`;
const SearchForm = styled.form`margin: 0;`;
const SearchBox = styled.div`
  ${gradientBorder}
  display: flex; align-items: center; gap: 12px; min-height: 56px; padding: 5px 16px; border-radius: 16px; background: white; color: #62776b;
  input { width: 100%; min-width: 0; border: 0; outline: 0; background: transparent; color: #191d1b; font-size: 16px; line-height: 1.5; padding: 10px 0; }
  input::-webkit-search-cancel-button { -webkit-appearance: none; }
  input::placeholder { color: #7a827d; }
  > svg { flex-shrink: 0; }
  button { flex-shrink: 0; border: 0; background: transparent; color: #56665d; width: 44px; height: 44px; font-size: 24px; padding: 0; }
  &:focus-within { box-shadow: 0 0 0 3px #d9f8e9; }
`;
const Directory = styled.section``;
const FilterButton = styled.button`display: inline-flex; align-items: center; gap: 10px; min-height: 44px; padding: 8px 12px; border: 1px solid #e0e6e2; border-radius: 10px; background: #fff; color: #414c45; font-size: 13px;`;
const FilterPanel = styled.div`padding: 0 16px 12px; margin-bottom: 20px; border: 1px solid #e0e6e2; border-radius: 14px;`;
const FilterOptions = styled.div`display: flex; justify-content: space-between; flex-wrap: wrap; gap: 8px; margin-top: 12px;`;
const Tabs = styled.div`
  display: flex; justify-content: space-between; gap: 12px; border-bottom: 1px solid #e8ece9; overflow-x: auto;
  button { flex-shrink: 0; display: inline-flex; align-items: center; gap: 7px; min-height: 48px; padding: 10px 0; border: 0; border-bottom: 2px solid transparent; background: transparent; color: #707872; font-size: 14px; font-weight: 600; white-space: nowrap; }
  button[aria-pressed="true"] { color: #171e1a; border-bottom-color: #1b3325; }
  span { font-size: 11px; border-radius: 12px; background: #f0f4f1; padding: 3px 6px; color: #617066; }
  @media (max-width: 599px) { gap: 10px; button { font-size: 12px; gap: 4px; } }
`;
const Toolbar = styled.div`
  display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 16px;
  h2 { font-size: 15px; font-weight: 650; margin: 0; scroll-margin-top: 20px; white-space: nowrap; } h2 span { color: #6f7872; font-weight: 500; margin-left: 5px; }
`;
const Selectors = styled.div`
  display: flex; gap: 8px;
  select { max-width: 140px; min-height: 44px; padding: 8px; border: 1px solid #e0e6e2; border-radius: 10px; background: #fff; color: #414c45; font-size: 13px; }
  @media (max-width: 359px) { gap: 4px; select { max-width: 100px; padding: 6px; } }
`;
const ContextNote = styled.p`margin: 12px 0 0; color: #727a74; font-size: 12px; line-height: 1.6;`;
const Grid = styled.div`
  display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 20px; align-items: stretch;
  ${({ $single }) => $single && "grid-template-columns: minmax(0, 700px);"}
  @media (max-width: 999px) { grid-template-columns: repeat(2, minmax(0, 1fr)); ${({ $single }) => $single && "grid-template-columns: minmax(0, 1fr);"} }
  @media (max-width: 599px) { grid-template-columns: minmax(0, 1fr); gap: 16px; }
`;
const Card = styled.article`
  min-width: 0; display: flex; flex-direction: column; border: 1px solid #e2e8e3; border-radius: 20px; background: #fff; padding: 16px 22px 22px;
  &:hover { border-color: #a5ccb4; box-shadow: 0 5px 20px #243e2908; }
  @media (max-width: 599px) { padding: 14px 20px 20px; }
`;
const CardMeta = styled.div`display: flex; align-items: center; justify-content: space-between; gap: 8px; margin-bottom: 4px; > div { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; font-size: 12px; color: #637269; }`;
const Freshness = styled.span`background: #e7fbef; color: #167044; padding: 4px 7px; border-radius: 6px; font-size: 10px; font-weight: 700;`;
const SaveButton = styled.button`display: inline-flex; align-items: center; justify-content: center; width: 44px; height: 44px; flex-shrink: 0; border: 0; border-radius: 50%; background: ${({ $saved }) => $saved ? "#e7fbef" : "#f5f7f5"}; color: ${({ $saved }) => $saved ? "#087641" : "#77847c"};`;
const CardLink = styled(Link)`display: flex; flex: 1; flex-direction: column; color: inherit; text-decoration: none; border-radius: 4px; h3 { margin: 6px 0 10px; font-size: 24px; letter-spacing: -.8px; line-height: 1.4; word-break: keep-all; overflow-wrap: anywhere; }`;
const Problem = styled.p`margin: 0 0 10px; font-size: 15px; font-weight: 600; color: #3f5146; line-height: 1.6; word-break: keep-all;`;
const Description = styled.p`font-size: 14px; color: #667168; line-height: 1.7; word-break: keep-all; overflow-wrap: anywhere; margin: 0 0 18px;`;
const Output = styled.p`margin: auto 0 0; font-size: 12px; color: #617167; line-height: 1.6; overflow-wrap: anywhere;`;
const Cost = styled.div`display: flex; flex-wrap: wrap; gap: 6px 12px; margin: 14px 0; font-size: 12px; line-height: 1.6; strong { color: #276643; font-weight: 600; } span { color: #707971; }`;
const CardAction = styled.span`display: flex; align-items: center; justify-content: space-between; min-height: 46px; border-radius: 24px; padding: 11px 16px; background: ${labsPalette.action}; color: white; font-size: 13px; font-weight: 600; span { font-size: 18px; color: ${labsPalette.arrow}; }`;
const EmptyState = styled.div`padding: 56px 24px; text-align: center; background: #f7f9f7; border-radius: 20px; h3 { font-size: 20px; line-height: 1.5; margin: 0 0 12px; word-break: keep-all; } p { font-size: 14px; color: #6c776f; line-height: 1.7; } button { margin-top: 12px; border: 1px solid #c9d6cd; border-radius: 24px; background: white; color: #244e35; padding: 12px 20px; min-height: 44px; font-size: 13px; }`;
const Pagination = styled.nav`display: flex; justify-content: center; align-items: center; gap: 24px; padding-top: 28px; color: #667269; font-size: 13px; button, select { border: 1px solid #dce4de; border-radius: 12px; min-height: 44px; padding: 10px 12px; background: #fff; color: #294c36; }`;
const Footer = styled.footer`border-top: 1px solid #e6ece8; margin-top: 48px; padding: 26px 0 12px; display: flex; justify-content: space-between; align-items: center; gap: 12px; flex-wrap: wrap; font-size: 13px; color: #7a817d; p { margin: 0; } a { display: inline-flex; align-items: center; min-height: 44px; gap: 14px; color: #3c5546; text-decoration: none; }`;
